/* eslint-disable @typescript-eslint/no-explicit-any -- The 'any' type is required here due to web component event typings that cannot be strictly typed. */
"use client";

import type { ReactNode } from "react";
import { useCallback, useReducer, useRef } from "react";

import type { AuthWrapperWcProperties } from "@/components/auth-wrapper/auth-wrapper-wc";
import type { ManageTripsAuthConfig } from "@jb-manage-trips/types/config";
import type {
  AuthWrapperCustomEvent,
  WcAuthWrapperElement,
} from "@jb-manage-trips/types/web-components";
import { DEFAULT_AUTH_CONTEXT, AuthContext, authReducer } from "./auth-context";
import { AuthWrapperWc } from "./auth-wrapper-wc";

export const AuthProvider = ({
  authConfig,
  children,
  fullStoryOrgId,
}: {
  authConfig: ManageTripsAuthConfig;
  children: ReactNode;
  fullStoryOrgId: string;
}) => {
  const webComponentReference = useRef<WcAuthWrapperElement>(null);

  const [authContext, dispatch] = useReducer(authReducer, DEFAULT_AUTH_CONTEXT);

  const onReady = useCallback(() => {
    webComponentReference.current?.start?.();
  }, []);

  const onActions = useCallback<AuthWrapperWcProperties["onActions"]>(
    (event: AuthWrapperCustomEvent<any>) => {
      dispatch(event.detail);
    },
    [],
  );

  const onResources = useCallback<AuthWrapperWcProperties["onResources"]>(
    (event: AuthWrapperCustomEvent<any>) => {
      dispatch(event.detail);
    },
    [],
  );

  return (
    <>
      <AuthWrapperWc
        clientId={authConfig.CLIENT_ID}
        excludedRoutes={authConfig.EXCLUDED_ROUTES.join(",")}
        fullStoryOrgId={fullStoryOrgId}
        handleReturnUrl={authConfig.HANDLE_RETURN_URL ? "true" : "false"}
        idleTimerDuration={authConfig.IDLE_TIMER_DURATION}
        isProduction={authConfig.IS_PRODUCTION ? "true" : "false"}
        issuer={authConfig.ISSUER}
        logoutWhenIdle={authConfig.LOGOUT_WHEN_IDLE ? "true" : "false"}
        onActions={onActions}
        onReady={onReady}
        onResources={onResources}
        redirectUri={authConfig.REDIRECT_URI}
        ref={webComponentReference}
        scriptSource={authConfig.WEB_COMPONENT_SRC}
      />

      <AuthContext.Provider value={authContext}>
        {children}
      </AuthContext.Provider>
    </>
  );
};

/* eslint-enable @typescript-eslint/no-explicit-any */
